// One warm-up run (issues #3473, #3523) as plain data the page keeps in state: the click timeline,
// the raw taps so far, the last clock reading and the progress those three imply. The singer sees
// a "Warm-up" bar at the head of a first session; internally it is still the calibration, because
// what it measures is the device's tap offset.
//
// The same shape as the exercise reducer - the page dispatches clock ticks and taps, the functions
// here are pure - so stories and tests drive it with a fake clock. Times are ms on the audio clock;
// taps arrive as stampTapMs readings, deliberately NOT aligned, because the alignment offset is
// what the run measures.
//
// All the measurement lives in warmUpProgress (common/src/rhythm_echo.ts): the clicks keep coming
// bar after bar and the run locks the moment warmUpLockTaps consecutive taps agree. Everything the
// screen draws is a selector over the progress, so a story can render any instant.

import { rhythmLevelParams } from '../../../../../common/src/learn_rules.ts';
import { LearnLevel } from '../../../../../common/src/model.ts';
import {
  rhythmBeatsPerBar,
  warmUpListenBars,
  WarmUpMark,
  WarmUpOutcome,
  warmUpProgress,
  WarmUpProgress,
  WarmUpTimeline,
  warmUpTimeline,
} from '../../../../../common/src/rhythm_echo.ts';
import { VisibleCue, visibleCue } from '../learn_cue.ts';
import { allLights, lightsForPlayback, RhythmLight } from './rhythm_blocks.tsx';

export const calibrationBpm = rhythmLevelParams[LearnLevel.Easy].bpm; // the clicks run at the Easy tempo

// listen: the first bar, heard and not tapped. tap: the clicks the singer taps with, however many
// bars it takes. locked / retry: the run is over, and the screen shows Next or Try again.
export type CalibrationPhase = 'listen' | 'tap' | 'locked' | 'retry';

export interface CalibrationRun {
  timeline: WarmUpTimeline;
  tapsMs: number[]; // raw tap stamps, in arrival order
  nowMs: number;
  progress: WarmUpProgress; // recomputed on every tick and tap, so the selectors stay cheap
}

function withProgress(run: Omit<CalibrationRun, 'progress'>): CalibrationRun {
  return { ...run, progress: warmUpProgress(run.timeline, run.tapsMs, run.nowMs) };
}

export function startCalibrationRun(nowMs: number): CalibrationRun {
  return withProgress({ timeline: warmUpTimeline(nowMs, calibrationBpm), tapsMs: [], nowMs });
}

// The clock now; the run ends by itself, on the lock or at the timeline's end
export function calibrationTick(run: CalibrationRun, nowMs: number): CalibrationRun {
  if (run.progress.outcome || nowMs === run.nowMs) {
    return run;
  }
  return withProgress({ ...run, nowMs });
}

// A tap; kept only while tapping is open, so a tap in the listen bar costs nothing. The gate reads
// the tap's own time, not the run's last tick, so a tap that lands in the gap between two frames -
// the frame before tapping opened and the one after - is not thrown away.
export function calibrationTap(run: CalibrationRun, tapMs: number): CalibrationRun {
  if (!isCalibrationTapOpen(run, tapMs)) {
    return run;
  }
  return withProgress({ ...run, tapsMs: [...run.tapsMs, tapMs] });
}

// ---- Selectors -------------------------------------------------------------------------------

export function calibrationOutcome(run: CalibrationRun): WarmUpOutcome | undefined {
  return run.progress.outcome;
}

export function isCalibrating(run: CalibrationRun): boolean {
  return calibrationOutcome(run) === undefined;
}

// The copy the screen shows. It turns to 'tap' on Go! - the first measured click - the way the
// pattern screen changes its copy on Go!, not when tapping opens half a beat earlier: the subtitle
// and the caption should not flip mid-cue.
export function calibrationPhase(run: CalibrationRun): CalibrationPhase {
  const outcome = calibrationOutcome(run);
  if (outcome) {
    return outcome.kind === 'locked' ? 'locked' : 'retry';
  }
  return run.nowMs >= run.timeline.cueMs.go ? 'tap' : 'listen';
}

// The tap-open rule over just the two things it needs, so the page can memoize its tap handler on
// them rather than on the whole run, which is a new object on every tick
export function isWarmUpTapOpen(timeline: WarmUpTimeline, outcome: WarmUpOutcome | undefined, tapMs: number): boolean {
  return outcome === undefined && tapMs >= timeline.tapsOpenMs;
}

// Whether a tap would count now: from half a beat before the first measured click - a keen first
// tap counts, as it does in a pattern - until the run ends. `nowMs` defaults to the run's last
// tick, but the page passes the tap's own time so a tap between two frames is judged at the moment
// it happened.
export function isCalibrationTapOpen(run: CalibrationRun, nowMs = run.nowMs): boolean {
  return isWarmUpTapOpen(run.timeline, run.progress.outcome, nowMs);
}

// The Ready · Set · Go! word pulsing now, over the last beats of the listen bar and the first
// measured click; the same cue every pattern uses
export function calibrationCue(run: CalibrationRun): VisibleCue | undefined {
  return visibleCue(run.nowMs, run.timeline.cueMs, run.timeline.beatMs);
}

// What one measured click's block shows, and it says the same thing as the pips: green only while
// its offset is one of the agreeing ones the run is counting, so four green blocks and four filled
// pips are the same fact. A tap that landed in the window but does not agree keeps the accent
// colour, and a click no tap came for goes dark (dark, not red: this is practice, not a test).
function markLight(mark: WarmUpMark): RhythmLight {
  switch (mark) {
    case 'agreeing':
      return 'green';
    case 'miss':
      return 'off';
    case 'matched':
    case 'pending':
      return 'on';
  }
}

// The lights for the four blocks, which show the bar sounding now: before the first click they are
// all off; through the listen bar each beat lights in the accent colour as its click sounds, the
// way a pattern's blocks fill as it plays; from the first tapping bar a sounded beat takes its
// mark's colour (see markLight); once the run locks they are all green.
export function calibrationLights(run: CalibrationRun): RhythmLight[] {
  if (calibrationOutcome(run)?.kind === 'locked') {
    return allLights(rhythmBeatsPerBar, 'green');
  }
  const { soundedIndex, marks } = run.progress;
  if (soundedIndex === undefined) {
    return allLights(rhythmBeatsPerBar, 'off');
  }
  const bar = Math.floor(soundedIndex / rhythmBeatsPerBar);
  const sounded = lightsForPlayback(rhythmBeatsPerBar, soundedIndex % rhythmBeatsPerBar);
  if (bar < warmUpListenBars) {
    return sounded;
  }
  const firstMark = (bar - warmUpListenBars) * rhythmBeatsPerBar;
  return sounded.map((light, beat) => (light === 'off' ? light : markLight(marks[firstMark + beat])));
}

// The bar of clicks the clock is in - 0 is the listen bar - or undefined through the silent lead
// beat before the first click. The page puts the clicks on the audio graph a bar at a time, keyed
// on this (issue #3523), so a run that stops being ticked stops clicking within a bar.
export function calibrationBar(run: CalibrationRun): number | undefined {
  const { soundedIndex } = run.progress;
  return soundedIndex === undefined ? undefined : Math.floor(soundedIndex / rhythmBeatsPerBar);
}

// The clicks of one bar of a run's timeline, in click order; empty past the last bar
export function calibrationBarClicksMs(timeline: WarmUpTimeline, bar: number): number[] {
  return timeline.clicksMs.slice(bar * rhythmBeatsPerBar, (bar + 1) * rhythmBeatsPerBar);
}

// How many of the warmUpLockTaps pips are filled: the agreeing offsets at the tail. The run locks
// the moment there are warmUpLockTaps of them, so the streak never holds more than the pips show.
export function calibrationStreak(run: CalibrationRun): number {
  return run.progress.streakMs.length;
}
